export type SessionUserRole = "SUPER_USER" | "ADMIN" | "AGENT";

export interface SessionJwtPayload {
  userId: string;
  orgId: string;
  role: SessionUserRole;
  iat?: number;
  exp?: number;
}

export interface StoredAuthUser {
  id: string;
  email: string;
  role: SessionUserRole | string;
}

const AUTH_USER_KEY = "authUser";

export const decodeAccessToken = (token?: string | null): SessionJwtPayload | null => {
  if (!token) return null;

  try {
    const [, payload] = token.split(".");
    if (!payload) return null;
    return JSON.parse(atob(payload)) as SessionJwtPayload;
  } catch {
    return null;
  }
};

/** True when a usable access token is stored (the router's logged-in check). */
export const hasAccessToken = (): boolean => {
  const token = localStorage.getItem("accessToken");
  return !!token && token !== "undefined" && token !== "null";
};

export const getSessionPayload = (): SessionJwtPayload | null => {
  if (typeof window === "undefined") return null;
  return decodeAccessToken(window.localStorage.getItem("accessToken"));
};

export const persistAuthUser = (user: StoredAuthUser): void => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
};

export const getStoredAuthUser = (): StoredAuthUser | null => {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(AUTH_USER_KEY);
    return raw ? (JSON.parse(raw) as StoredAuthUser) : null;
  } catch {
    return null;
  }
};

export const clearAuthSession = (): void => {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem("accessToken");
  window.localStorage.removeItem("refreshToken");
  window.localStorage.removeItem(AUTH_USER_KEY);
};

export const isSuperUserRole = (role?: string | null): boolean => role === "SUPER_USER";

export const isAdminLikeRole = (role?: string | null): boolean =>
  role === "SUPER_USER" || role === "ADMIN";
